/**
 * components/landing/trust/data.js — Phase 11 · Trust Center copy.
 *
 * ─── DESIGN PRINCIPLE ────────────────────────────────────────────
 * The Trust Center is the highest-stakes page on the marketing site
 * from a legal/compliance perspective, so every line here is written
 * with a strict "no overclaim" discipline:
 *
 *   • Certifications we haven't earned yet → "in progress" / "on roadmap"
 *   • Frameworks we're not in scope for    → "not currently in scope"
 *   • Aspirational architecture claims      → phrased as commitments
 *   • Policy documents not yet published    → "available on request"
 *
 * All strings live here so PRD edits are one-file operations.
 */

// ── §11.2 LIVE STATUS (mocked green-pill band) ───────────────────
export const STATUS_REGIONS = [
  { id: 'global', label: 'Global control plane', region: 'multi-region', status: 'operational' },
  { id: 'ap',     label: 'Asia-Pacific',          region: 'ap-south-1',   status: 'operational' },
  { id: 'eu',     label: 'Europe',                region: 'eu-west-1',    status: 'operational' },
  { id: 'us',     label: 'North America',         region: 'us-east-1',    status: 'operational' },
]

export const STATUS_METRICS = [
  { id: 'q',   label: 'Uptime · trailing 90 days', value: '99.98%'         },
  { id: 'y',   label: 'Uptime · trailing 12 months', value: '99.95%'       },
  { id: 'inc', label: 'Sev-1 incidents · last quarter', value: '0'          },
  { id: 'mttr',label: 'Mean time to recovery',     value: '< 15 min'       },
]

// ── §11.3 SECURITY OVERVIEW — 6 pillars ──────────────────────────
export const SECURITY_PILLARS = [
  {
    id: 'defense',   label: 'Defence in depth',       iconKey: 'ShieldCheck', tone: 'orange',
    body: 'Multiple, independent layers of controls across network, application, data and identity — designed so a single misconfiguration cannot become a breach.',
  },
  {
    id: 'encryption',label: 'Encryption everywhere',   iconKey: 'Lock',       tone: 'green',
    body: 'TLS 1.2+ for every request in transit and AES-256 at rest for every persisted store — including databases, object storage and backups.',
  },
  {
    id: 'isolation', label: 'Tenant isolation',        iconKey: 'Layers',     tone: 'cyan',
    body: 'Every tenant lives in a logically isolated schema and object-storage prefix, with row-level security and tenant-scoped keys enforced at the fabric layer.',
  },
  {
    id: 'access',    label: 'Access control · RBAC + SSO', iconKey: 'KeyRound', tone: 'blue',
    body: 'Role-based access, single sign-on (SAML / OIDC on Enterprise), least-privilege internal roles and short-lived credentials for all engineering access.',
  },
  {
    id: 'monitor',   label: 'Monitoring & audit',      iconKey: 'Activity',   tone: 'purple',
    body: 'Every privileged action is captured in an append-only audit log.  Anomaly detection and paging are wired to production 24×7 for Sev-1 and Sev-2 events.',
  },
  {
    id: 'response',  label: 'Incident response',       iconKey: 'Siren',      tone: 'rose',
    body: 'A documented incident response plan with clear severity ladders, on-call rotation, customer-notification SLAs and blameless post-mortems for every incident.',
  },
]

// ── §11.4 PRIVACY COMMITMENT — 4 principles ──────────────────────
export const PRIVACY_PRINCIPLES = [
  {
    id: 'minimisation', label: 'Data minimisation',    iconKey: 'Filter',   tone: 'orange',
    body: 'We collect only what is needed to deliver the service and honour a contract with you — never for onward sale or advertising.',
  },
  {
    id: 'purpose',      label: 'Purpose limitation',    iconKey: 'Target',   tone: 'blue',
    body: 'Personal data is processed for the purposes documented in our DPA and Privacy Policy — nothing else.  Purpose changes require a new lawful basis.',
  },
  {
    id: 'rights',       label: 'User rights',            iconKey: 'UserCheck', tone: 'green',
    body: 'Data subjects can exercise access, rectification, erasure, restriction, portability and objection rights via a documented workflow — with a 30-day SLA.',
  },
  {
    id: 'transparency', label: 'Transparent processing', iconKey: 'Eye',      tone: 'purple',
    body: 'A published sub-processor list, incident-notification commitments and change-log means you always know who processes your data and why.',
  },
]

// ── §11.5 DATA LIFECYCLE — 5 stages ──────────────────────────────
export const DATA_LIFECYCLE = [
  {
    id: 'ingest',   label: 'Ingestion',        iconKey: 'ArrowDownToLine', tone: 'orange',
    body: 'Data enters the platform over TLS 1.2+ via authenticated APIs.  Payloads are validated and sanitised before being persisted.',
    detail: 'TLS 1.2+ · signed requests · schema validation',
  },
  {
    id: 'store',    label: 'Storage',           iconKey: 'Database',       tone: 'green',
    body: 'Persisted in tenant-scoped, encrypted stores.  Database and object storage encryption keys are managed independently per environment.',
    detail: 'AES-256 at rest · tenant-scoped keys · encrypted backups',
  },
  {
    id: 'process',  label: 'Processing',         iconKey: 'Cpu',           tone: 'blue',
    body: 'Only the smallest set of services required for a given feature has access.  All access is logged and reviewed on a rotating cadence.',
    detail: 'Least-privilege IAM · audit log · rotation reviews',
  },
  {
    id: 'retain',   label: 'Retention',          iconKey: 'History',       tone: 'purple',
    body: 'Retention windows are configurable per data class and per tenant.  Defaults are documented; overrides land in the DPA on request.',
    detail: 'Configurable per data class · documented defaults',
  },
  {
    id: 'delete',   label: 'Deletion',           iconKey: 'Trash2',        tone: 'rose',
    body: 'Right-to-erasure requests are honoured on a 30-day SLA.  On tenant termination, all customer data is deleted or returned per the MSA.',
    detail: '30-day erasure SLA · MSA termination workflow',
  },
]

// ── §11.6 PLATFORM AVAILABILITY — SLA-flavoured facts ────────────
export const AVAILABILITY_FACTS = [
  { id: 'target', label: 'Uptime target',        value: '99.9% monthly',       tone: 'green',   body: 'Contractual target on Professional and Enterprise licences.  Higher targets available under enterprise agreements.' },
  { id: 'rpo',    label: 'RPO (data loss window)', value: '≤ 15 minutes',       tone: 'blue',   body: 'Point-in-time recovery for primary data stores with < 15-minute recovery point objective for the production fabric.' },
  { id: 'rto',    label: 'RTO (recovery time)',   value: '≤ 60 minutes',       tone: 'purple', body: 'Recovery time objective for Sev-1 events under the documented incident-response playbook.' },
  { id: 'backup', label: 'Backups',                value: 'Daily · 30-day',    tone: 'cyan',    body: 'Encrypted, region-redundant snapshots retained for 30 days by default.  Longer retention available on request.' },
  { id: 'dr',     label: 'Disaster recovery',      value: 'Documented DR plan', tone: 'amber',  body: 'A tested DR plan with quarterly failover drills.  Multi-region active-active is on the 2026 roadmap for Enterprise-tier tenants.' },
  { id: 'sla',    label: 'SLA & credits',           value: 'MSA · Enterprise', tone: 'orange',  body: 'Formal SLA with service-credit remedies is provided in the Master Services Agreement for Enterprise-tier tenants.' },
]

// ── §11.7 COMPLIANCE POSTURE — honest, no overclaim ──────────────
export const COMPLIANCE_ITEMS = [
  { id: 'gdpr',   label: 'GDPR',                     iconKey: 'ScrollText',  tone: 'blue',   status: 'aligned',     body: 'Data-subject-rights workflow, DPA and standard contractual clauses available for EU customers on Enterprise licences.' },
  { id: 'iso',    label: 'ISO / IEC 27001',           iconKey: 'BadgeCheck',  tone: 'purple', status: 'roadmap',     body: 'Gap analysis complete.  Formal certification programme on the compliance roadmap.' },
  { id: 'pci',    label: 'PCI-DSS',                   iconKey: 'CreditCard',  tone: 'green',  status: 'not-in-scope',body: 'Kalki Ai Cloud does not store, process or transmit primary account numbers.  Card data flows are handled by our PCI-compliant payments provider.' },
  { id: 'hipaa',  label: 'HIPAA',                     iconKey: 'HeartPulse',  tone: 'rose',   status: 'not-in-scope',body: 'The platform is not currently offered under a BAA and is not intended for the processing of PHI.' },
  { id: 'dpa',    label: 'Sub-processor programme',    iconKey: 'Users',      tone: 'cyan',   status: 'documented',  body: 'Vetted sub-processor list with contracted DPAs.  Material changes are notified in advance under the DPA.' },
]

export const COMPLIANCE_LABELS = {
  'in-progress':  { label: 'In progress',    dot: '#FFC107' },
  'roadmap':      { label: 'On roadmap',      dot: '#a855f7' },
  'aligned':      { label: 'Aligned',         dot: '#22c55e' },
  'documented':   { label: 'Documented',      dot: '#22d3ee' },
  'not-in-scope': { label: 'Not in scope',    dot: '#71717a' },
}

// ── §11.8 LEGAL POLICIES — placeholder cards for future sub-pages ─
// Each policy is a placeholder marketing card that will link to the
// canonical policy document once published.  Until then, cards show
// an "Available on request" badge and route to /trust/<slug>.
export const LEGAL_POLICIES = [
  { id: 'terms',   label: 'Terms of Service',        href: '/trust/terms',        iconKey: 'ScrollText', tone: 'orange', body: 'The master commercial terms under which the Kalki Ai Cloud platform is made available to customers and users.' },
  { id: 'privacy', label: 'Privacy Policy',          href: '/trust/privacy',      iconKey: 'Lock',       tone: 'blue',   body: 'How we collect, use and protect personal data — and how data subjects can exercise their rights under GDPR and similar regimes.' },
  { id: 'dpa',     label: 'Data Processing Addendum', href: '/trust/dpa',          iconKey: 'FileSignature',tone: 'purple', body: 'Contractual GDPR/UK-GDPR data-processing terms, including standard contractual clauses for international transfers.' },
  { id: 'aup',     label: 'Acceptable Use Policy',   href: '/trust/aup',          iconKey: 'ShieldAlert',tone: 'rose',   body: 'The behaviour we expect from operators and end-users on the platform — and the categories of activity that are prohibited.' },
  { id: 'cookies', label: 'Cookie Policy',           href: '/trust/cookies',      iconKey: 'Cookie',     tone: 'amber',  body: 'The cookies and similar technologies used across the Kalki Ai Cloud marketing site and platform, and how to control them.' },
  { id: 'sub',     label: 'Sub-processors',           href: '/trust/subprocessors',iconKey: 'Users',      tone: 'cyan',   body: 'The list of vetted sub-processors that support the delivery of the Kalki Ai Cloud platform, with jurisdictions and purposes.' },
]

// ── §11.9 SECURITY FAQ ───────────────────────────────────────────
export const TRUST_FAQ = [
  { q: 'What is your compliance posture?',
    a: 'Kalki Ai Cloud is GDPR-aligned and operates under a documented sub-processor programme.  ISO/IEC 27001 certification is on the roadmap.  Enterprise procurement questionnaires, penetration-test summaries and vendor security packets are available under NDA.' },
  { q: 'How is my data isolated from other tenants?',
    a: 'Every tenant is provisioned into a logically isolated schema and object-storage prefix.  Row-level security and tenant-scoped keys are enforced at the platform fabric layer — application code does not opt in to isolation, it is a property of the runtime.' },
  { q: 'Do you sign a Data Processing Addendum (DPA)?',
    a: 'Yes.  Our standard DPA — including Standard Contractual Clauses (SCCs) for EU/UK transfers — is available on request for Professional and Enterprise licences.' },
  { q: 'Where is data hosted?',
    a: 'The primary production fabric is hosted in ap-south-1 with regional replicas in eu-west-1 and us-east-1.  Data-residency options for enterprise customers can be scoped as part of the MSA.' },
  { q: 'Can I get a copy of your security policies?',
    a: 'Selected internal policies, our vendor-security questionnaire response and pen-test summary letters are available under NDA to prospects that have progressed to procurement stage.' },
  { q: 'Do you support SSO / SAML / SCIM?',
    a: 'SSO via SAML 2.0 and OIDC is available on Enterprise licences.  SCIM for automated user provisioning and deprovisioning is on the 2026 roadmap.' },
  { q: 'What happens to my data if we churn?',
    a: 'On termination of the MSA, customer data is deleted or returned within the timelines specified in the DPA and MSA — with a written attestation of destruction on request.' },
  { q: 'How do you use AI models with our data?',
    a: 'We use vendor-neutral AI providers under a zero-retention posture where available, and never share tenant data across tenants.  Model training on customer data is off by default and requires explicit opt-in.' },
]
